import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { Logo } from "./logo";
import { UiLink } from "./router";
import { ThemeToggle } from "./theme-toggle";

export type AuthPanel = {
  headline: string;
  sub: string;
  bullets: string[];
};

const defaultPanel: AuthPanel = {
  headline: "One account for every application in the suite.",
  sub: "Signing in here signs you in everywhere.",
  bullets: [
    "A wallet and usage record per application",
    "Approve every new device that installs an app",
    "Every device signed in on your account, in one place",
  ],
};

/**
 * The split layout every authentication screen shares: a dark brand panel from
 * `lg` up, and a centred form column that stands alone on small screens.
 *
 * Purely presentational — it renders whatever the screen hands it as
 * `children`. Field state, validation and submission stay in the screen, which
 * is what keeps `src/ui/` free of the session and query hooks.
 */
export function AuthShell({
  title,
  subtitle,
  panel = defaultPanel,
  footer,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  panel?: AuthPanel;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)]">
      <section className="relative hidden overflow-hidden bg-linear-150 from-[#2b3e96] to-[#131c46] p-10 text-white lg:flex lg:flex-col xl:p-14">
        <span
          aria-hidden
          className="absolute -top-32 -right-24 size-96 rounded-full bg-white/8 blur-3xl"
        />
        <span
          aria-hidden
          className="absolute -bottom-40 -left-20 size-96 rounded-full bg-[#7c97ff]/20 blur-3xl"
        />

        <UiLink href="/" className="relative flex items-center gap-3">
          <Logo className="size-10" />
          <span className="font-display text-md font-semibold">Empite Suite</span>
        </UiLink>

        <div className="relative mt-auto">
          <h2 className="max-w-md font-display text-4xl font-bold tracking-[-0.03em]">
            {panel.headline}
          </h2>
          <p className="mt-4 max-w-sm text-base text-[#a9b6e4]">{panel.sub}</p>

          <ul className="mt-8 space-y-3.5">
            {panel.bullets.map((bullet) => (
              <li key={bullet} className="flex items-start gap-3 text-sm text-[#c2ccec]">
                <span className="mt-0.5 grid size-4.5 shrink-0 place-items-center rounded-full bg-white/12">
                  <Check className="size-3 text-[#7c97ff]" aria-hidden />
                </span>
                {bullet}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative mt-auto pt-10 text-2xs text-[#8b97c9]">
          © 2026 Empite. Demo environment.
        </p>
      </section>

      <section className="flex flex-col px-5 py-8 sm:px-10">
        <div className="flex items-center justify-between">
          <UiLink href="/" className="flex items-center gap-2.5 lg:invisible">
            <Logo className="size-8" />
            <span className="font-display text-sm font-semibold text-ink">
              Empite Suite
            </span>
          </UiLink>
          <ThemeToggle />
        </div>

        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10">
          <h1 className="font-display text-2xl font-bold tracking-[-0.02em] text-ink">
            {title}
          </h1>
          {subtitle ? <p className="mt-1.5 text-base text-ink-3">{subtitle}</p> : null}
          {children}
          {footer ? <div className="mt-8">{footer}</div> : null}
        </div>
      </section>
    </div>
  );
}
